import React, { useState } from 'react';
import './QuestionnaireStagesHost.scss';
import {
    DataRequestProject,
    DataRequestProjectState,
} from '../../../models/DataRequestHub/DataRequestProject';
import QuestionnaireStagesList from '../QuestionnaireStagesList/QuestionnaireStagesList';
import { QuestionnaireModuleHost } from '../DataRequestDashboardPage/QuestionnaireModuleHost/QuestionnaireModuleHost';
import { useNavigate } from 'react-router-dom';
import { DesignModeSwitcher } from '../DesignModeSwitcher/DesignModeSwitcher';
import QuestionSeenStatus from '../../../models/DataRequestHub/QuestionSeenStatus';
import { EditorModeEnum } from '../../../models/DataRequestHub/ProjectEditorModeEnum';
import { EditorTypeEnum } from '../../../models/DataRequestHub/ProjectEditorEnum';
import moment from 'moment';
import createNotification from '../../../utils/createNotification';
import { useStateSelector } from '../../../store/selectors';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useDataRequestUrl } from '../../../hooks/useDataRequestDashboardUrl';
import { PopupsWizard } from '../../../components/PopupsWizard/PopupsWizard';
import { SnapshotsPopup } from '../SnapshotsPopup/SnapshotsPopup';
import { DataForm } from '../../../models/DataRequestHub/DataForm';
import { QuestionnaireFormPopup } from '../QuestionnaireFormPopup/QuestionnaireFormPopup';
import { useIsDataRequestProjectReadonly } from '../../../custom-hooks/useIsDataRequestProjectReadonly';
import { ModalHeader, ModalBody } from 'reactstrap';
import Rodal from '../../../components/Rodal/Rodal';

interface QuestionnaireStagesHostProps {
    projects: DataRequestProject[];
    isAdminView: boolean;
    isReadOnly?: boolean;
    updateProjectsData(isInitialFetch?: boolean): void;
    preselectedQuestionnaireFormId: number;
    questionsSeenStatus: QuestionSeenStatus[];
    markQuestionsAsSeen(
        formId: number,
        questionsIds: number[],
        subQuestionsIds: number[]
    ): void;
    isBuiltIn: boolean;
    isSingleitemProject?: boolean;
    engagementId?: number;
}

const QuestionnaireStagesHost = (props: QuestionnaireStagesHostProps) => {
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] =
        useState<number>(props.preselectedQuestionnaireFormId);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const axios = useStateSelector((state) => state.core.axios);
    const editorUrl = useDataRequestUrl(true);
    const [isSnapshotsPopupVisible, setIsSnapshotsPopupVisible] =
        useState(false);
    const questionsSeenStatus = useStateSelector(
        (state) => state.dataRequestsNotifications.questionsSeenStatus
    );
    const [selectedSnaphotForm, setSelectedSnaphotForm] = useState<DataForm>();
    const [selectedProject, setSelectedProject] =
        useState<DataRequestProject | null>(null);
    const isReadonlyProject = useIsDataRequestProjectReadonly(props.projects, props.engagementId) || !!props.isReadOnly;
    const [exportCommentsDialogOpen, setExportCommentsDialogOpen] =
        useState(false);
    const [includeMemberQuestions, setIncludeMemberQuestions] = useState(true);
    const [includeVvComments, setIncludeVvComments] = useState(true);
    const [exportingQuestionnaireFormId, setExportingQuestionnaireFormId] =
        useState<number | null>(null);

    const handleStageEditIconClicked = (formId: number) => {
        const targetProject =
            props.projects.find((project) =>
                project.dataForms.some((form) => form.id === formId)
            ) ?? props.projects.find((project) => project);

        if (targetProject) {
            navigate(`/${editorUrl}`, {
                state: {
                    questionnaireEditorParams: {
                        targetQuestionnaireId: formId,
                    },
                    project: targetProject,
                    shouldNavigateToProjectViewMode: true,
                    type: EditorTypeEnum.Questionnaire,
                    mode: EditorModeEnum.EditProject,
                    additionalUrlToReturn: `?tab=${
                        EditorTypeEnum.Questionnaire + 1
                    }`,
                    isSingleitemProject: props.isSingleitemProject,
                    isExternalProject: targetProject.isExternalProject,
                },
            });
        }
    };

    const renderStagesList = () => (
        <QuestionnaireStagesList
            onStageNameClick={setSelectedQuestionnaireId}
            onEditIconClick={handleStageEditIconClicked}
            isEditorMode={false}
            invalidStageIds={[]}
            isSingleitemProject={props.isSingleitemProject}
            {...props}
        />
    );

    const isProjectActive = (formId: number) => {
        const targetProject = getProjectByFormId(formId);

        return (
            targetProject.state === DataRequestProjectState.Active &&
            !isReadonlyProject
        );
    };

    const getProject = (projectId: number) => {
        return props.projects.find((x) => x.id === projectId);
    };

    const getProjectByFormId = (formId: number) => {
        return (
            props.projects.find((project) =>
                project.dataForms.some((form) => form.id === formId)
            ) ?? props.projects.find((project) => project)
        );
    };

    const renderSnapshotPopup = () => {
        const targetProject = getProjectByFormId(selectedQuestionnaireId);
        const questionnaireForm = getQuestionnaireForm();

        let popupHeader =
            questionnaireForm.customName ??
            questionnaireForm.originalTemplateName + ' - Data Snapshots';
        let snapshotFileName = `${targetProject.practiceName} - ${
            questionnaireForm.customName ??
            questionnaireForm.originalTemplateName
        }`;

        return (
            <>
                <PopupsWizard
                    popups={[
                        <SnapshotsPopup
                            projectId={targetProject.id}
                            objectId={questionnaireForm.id}
                            header={popupHeader}
                            setSelectedForm={(form: DataForm) => {
                                setSelectedSnaphotForm(form);
                                const targetProject = getProjectByFormId(
                                    form?.id
                                );
                                setSelectedProject(targetProject);
                            }}
                            onClose={() => setIsSnapshotsPopupVisible(false)}
                            isArchiveMode={false}
                            snapshotFileName={snapshotFileName}
                            isAdminView={props.isAdminView}
                            isReadonlyMode={isReadonlyProject || !!props.isReadOnly}
                            mode="form"
                        />,

                        <QuestionnaireFormPopup
                            header={
                                selectedSnaphotForm?.customName ??
                                selectedSnaphotForm?.originalTemplateName
                            }
                            isAdminView={props.isAdminView}
                            form={selectedSnaphotForm}
                            project={selectedProject}
                            isReadonlyMode={true}
                            onlyShowQuestionColumns={!props.isAdminView}
                            updateProjectsData={() => {}}
                            questionsSeenStatus={questionsSeenStatus}
                        />,
                    ]}
                />
            </>
        );
    };

    const closeExportCommentsDialog = () => {
        setExportCommentsDialogOpen(false);
        setIncludeMemberQuestions(true);
        setIncludeVvComments(true);
    };

    const renderExportOption = (
        name: string,
        label: string,
        value: boolean,
        onChange: (value: boolean) => void
    ) => (
        <div className="config-section">
            <label className="config-label">{label}</label>
            <div className="radio-group">
                {[
                    { text: 'Yes', optionValue: true },
                    { text: 'No', optionValue: false },
                ].map((option) => {
                    const id = `${name}-${option.text.toLowerCase()}`;
                    return (
                        <div className="custom-control-radio" key={id}>
                            <input
                                type="radio"
                                id={id}
                                name={name}
                                className="form-check-input"
                                checked={value === option.optionValue}
                                onChange={() => onChange(option.optionValue)}
                            />
                            <label htmlFor={id} className="bullet">
                                <span className="bullet-selected"></span>
                            </label>
                            <label className="label-text" htmlFor={id}>
                                {option.text}
                            </label>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const getQuestionnaireSnapshotFile = (
        questionnaireFormId: number,
        includeVvCommentsParam: boolean,
        includeMemberQuestionsParam: boolean
    ) => {
        setIsLoading(true);
        const targetProject =
            props.projects.find((project) =>
                project.dataForms.some(
                    (form) => form.id === questionnaireFormId
                )
            ) ?? props.projects.find((project) => project);

        const requestParamsObj = {
            params: {
                snapshotFormId: questionnaireFormId,
                includeAllTables: false,
                includeVvComments: includeVvCommentsParam,
                includeMemberQuestions: includeMemberQuestionsParam,
            },
            responseType: 'blob' as const,
        };
        axios
            .get(
                `api/dataRequestProjects/${targetProject.id}/dataForms/${questionnaireFormId}/dataFormSnapshots/file`,
                requestParamsObj
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while downloading file',
                        'error'
                    );
                } else {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    );

                    const snapshoDateStringFormat = moment(new Date()).format(
                        'MM-DD-YYYY'
                    );
                    const targetForm = targetProject.dataForms.find(
                        (x) => x.id === questionnaireFormId
                    );
                    const fileName = `${targetProject.practiceName} - ${targetForm.customName} ${snapshoDateStringFormat}.xlsx`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const renderStageView = () => {
        const projectId = props.projects.find((f) =>
            f.dataForms.some((s) => s.id === selectedQuestionnaireId)
        ).id;
        const questionnaireForm = getQuestionnaireForm();
        const projectVariables = props.projects.find((f) =>
            f.dataForms.some((s) => s.id === selectedQuestionnaireId)
        ).variables;
        return (
            <>
                <div className="questionnaire-stages-host-header">
                    <div className="stage-navigation-breadcrumb">
                        <label
                            className="link-text"
                            onClick={() => setSelectedQuestionnaireId(null)}>
                            Questionnaire
                        </label>
                        <label className="node-separator">{' / '}</label>
                        <label className="link-text">
                            {questionnaireForm.customName}
                        </label>
                    </div>
                    {props.isAdminView && (
                        <div className="right-header">
                            <div
                                className="link-text"
                                onClick={() =>
                                    setIsSnapshotsPopupVisible(true)
                                }>
                                Data Snapshots
                            </div>
                            <ButtonLoader
                                type="submit"
                                className={'btn btn-primary'}
                                disabled={isLoading}
                                isLoading={isLoading}
                                loaderButtonText={''}
                                buttonText={'Export to Excel'}
                                onClick={() => {
                                    setExportingQuestionnaireFormId(
                                        questionnaireForm.id
                                    );
                                    setExportCommentsDialogOpen(true);
                                }}
                            />
                            {isProjectActive(questionnaireForm.id) && (
                                <DesignModeSwitcher
                                    defaultValue={false}
                                    onEnable={handleStageEditIconClicked}
                                    formId={questionnaireForm.id}
                                />
                            )}
                        </div>
                    )}
                </div>

                <QuestionnaireModuleHost
                    key={'questionnaire-view-form'}
                    isAdminView={props.isAdminView}
                    isReadonlyMode={isReadonlyProject || !!props.isReadOnly}
                    projectId={projectId}
                    questionnaireForm={questionnaireForm}
                    updateProjectsData={props.updateProjectsData}
                    projectVariables={projectVariables}
                    markQuestionsAsSeen={props.markQuestionsAsSeen}
                    questionsSeenStatus={props.questionsSeenStatus}
                    project={getProject(projectId)}
                    isBuiltIn={props.isBuiltIn}
                />
            </>
        );
    };

    const getQuestionnaireForm = () =>
        props.projects
            .flatMap((project) => project.dataForms)
            .find((form) => form.id === selectedQuestionnaireId);

    return (
        <div className="questionnaire-stages-host">
            {selectedQuestionnaireId ? renderStageView() : renderStagesList()}
            {selectedQuestionnaireId && isSnapshotsPopupVisible
                ? renderSnapshotPopup()
                : []}

            {exportCommentsDialogOpen && (
                <Rodal
                    visible={true}
                    onClose={closeExportCommentsDialog}
                    className="export-comments-popup"
                    width={498}
                    animation="fade"
                    closeMaskOnClick={false}>
                    <ModalHeader>Export to Excel</ModalHeader>
                    <ModalBody>
                        <label className="section-title">
                            Export Configuration
                        </label>
                        {renderExportOption(
                            'export-member-questions',
                            'Include member questions?',
                            includeMemberQuestions,
                            setIncludeMemberQuestions
                        )}
                        {renderExportOption(
                            'export-vv-comments',
                            'Include VetValue comments?',
                            includeVvComments,
                            setIncludeVvComments
                        )}
                        <div className="buttons-wrapper">
                            <button
                                className="btn btn-cancel"
                                onClick={closeExportCommentsDialog}>
                                Cancel
                            </button>
                            <ButtonLoader
                                className="btn btn-primary"
                                buttonText="Export"
                                loaderButtonText=""
                                isLoading={isLoading}
                                disabled={isLoading}
                                onClick={() => {
                                    if (
                                        exportingQuestionnaireFormId !== null
                                    ) {
                                        getQuestionnaireSnapshotFile(
                                            exportingQuestionnaireFormId,
                                            includeVvComments,
                                            includeMemberQuestions
                                        );
                                    }
                                    closeExportCommentsDialog();
                                }}
                            />
                        </div>
                    </ModalBody>
                </Rodal>
            )}
        </div>
    );
};

export default QuestionnaireStagesHost;
